import React, { useState, useMemo, useCallback, useEffect } from "react";
import { withAuth } from "../api/auth.ts";
import {
  Box,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Typography,
  Paper,
  IconButton,
  Stack,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  Fade,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Divider,
  Checkbox,
  Select,
  FormControl,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import {
  Search,
  ChevronRight,
  Folder,
  CreateNewFolder as NewFolderIcon,
  FileUpload as UploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  InfoOutlined as InfoIcon,
  CloudUpload as CloudUploadIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  Close as CloseIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Menu as MenuIcon,
  CheckBoxOutlined as SelectIcon,
  Download as DownloadIcon,
  DeleteSweep as BatchDeleteIcon,
  ArrowUpward as AscIcon,
  ArrowDownward as DescIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
} from "@mui/icons-material";
import WidgetsIcon from "@mui/icons-material/Widgets";
import { keyframes } from "@mui/system";
import { usePaletteColors } from "../hooks/useDarkMode.ts";
import { ItemCard } from "./ItemCard.tsx";
import { FileThumbnail } from "./FileThumbnail.tsx";
import { FileTypeIcon } from "./FileTypeIcon.tsx";
import { PopButton } from "./PopButton.tsx";
import { getMyPaletteColor } from "../utils/colorUtils.ts";
import { formatFileSize, formatDate } from "../utils/formatUtils.ts";
import {
  FileListResponse,
  BreadcrumbDto,
  FileFolderDto,
  FileItemDto,
  SearchResponse,
  SearchFileItem,
  SearchFolderItem,
} from "./FileManager.tsx";

interface MainContentProps {
  hpcode: number;
  effectiveRoomId: number;
  honbuId: number;
  data: FileListResponse | null;
  isLoading: boolean;
  onFolderClick: (id: string | null) => void;
  onFileClick: (file: FileItemDto) => void;
  onShowDetail: (file: FileItemDto) => void;
  onRefresh: () => Promise<void>;
  onCreateFolderClick: () => void;
  onUploadClick: () => void;
  onDeleteItem: (id: string, type: "folder" | "file") => void;
  onMoveFile: (fileId: string, targetFolderId: string) => void;
  onUploadFiles: (files: File[]) => void;
  onToggleBookmark: (id: string, type: "folder" | "file") => void;
  onContextMenu: (
    event: React.MouseEvent,
    item: any,
    type: "file" | "folder",
  ) => void;
  viewMode: "grid" | "thumbnail" | "list";
  onViewModeChange: (mode: "grid" | "thumbnail" | "list") => void;
  onRenameClick: (id: string, name: string, type: "folder" | "file") => void;
  isMobile?: boolean;
  onMenuClick?: () => void;
  // 選択モード用
  isSelectionMode?: boolean;
  selectedItems?: Array<{ id: string; type: "folder" | "file" }>;
  onToggleSelection?: (id: string, type: "folder" | "file") => void;
  onEnterSelectionMode?: () => void;
  onExitSelectionMode?: () => void;
  onBulkDownload?: () => void;
  onBulkDelete?: () => void;
  onOpenPermission?: (
    folderId: string,
    folderName: string,
    isRestricted: boolean,
  ) => void;
}

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

export const MainContent = ({
  hpcode,
  effectiveRoomId,
  honbuId,
  data,
  isLoading,
  onFolderClick,
  onFileClick,
  onShowDetail,
  onCreateFolderClick,
  onUploadClick,
  onDeleteItem,
  onMoveFile,
  onUploadFiles,
  onToggleBookmark,
  onContextMenu,
  viewMode,
  onViewModeChange,
  onRenameClick,
  isMobile = false,
  onMenuClick,
  isSelectionMode = false,
  selectedItems = [],
  onToggleSelection,
  onEnterSelectionMode,
  onExitSelectionMode,
  onBulkDownload,
  onBulkDelete,
  onOpenPermission,
}: MainContentProps) => {
  const params = new URLSearchParams(window.location.search);
  const palette = getMyPaletteColor(params.get("color"));
  const { isDarkMode, bgColor, accentColor } = usePaletteColors(palette);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuTarget, setMenuTarget] = useState<any>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const [isNativeDragging, setIsNativeDragging] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResponse | null>(
    null,
  );
  const [isSearching, setIsSearching] = useState(false);
  // サムネイル大表示で読み込みに失敗したファイル ID（アイコン表示にフォールバック）
  const [thumbErrorIds, setThumbErrorIds] = useState<Set<string>>(new Set());

  // 検索クエリが変わったら全フォルダ横断検索APIを呼ぶ (300msデバウンス)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await fetch(
          `/api/files/search?hpcode=${hpcode}&roomId=${effectiveRoomId}&honbuId=${honbuId}&q=${encodeURIComponent(searchQuery.trim())}`,
        );
        if (res.ok) {
          setSearchResults(await res.json());
        }
      } catch {
        // 無視
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, hpcode, effectiveRoomId, honbuId]);

  type SortKey = "fileName" | "uploaderName" | "updatedAt" | "fileSize";
  type SortDirection = "asc" | "desc";
  const [sortKey, setSortKey] = useState<SortKey>("updatedAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const sortKeyLabels: Record<SortKey, string> = {
    fileName: "名前",
    uploaderName: "アップロード",
    updatedAt: "更新日",
    fileSize: "サイズ",
  };

  const handleSort = useCallback(
    (key: SortKey) => {
      setSortDirection((prev) =>
        sortKey === key ? (prev === "asc" ? "desc" : "asc") : "desc",
      );
      setSortKey(key);
    },
    [sortKey],
  );

  const isItemSelected = (id: string) =>
    selectedItems.some((item) => item.id === id);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setMenuTarget(item);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuTarget(null);
  };

  // サムネイル取得 URL (thumbnailStatus=2 [非対応・生成失敗] のファイルはリクエストしない)
  const getThumbnailUrl = (file: FileItemDto): string | undefined =>
    file.thumbnailStatus !== 2
      ? withAuth(`/api/files/thumbnail/${file.fileId}?honbuId=${honbuId}&roomId=${effectiveRoomId}`)
      : undefined;

  const getItemIcon = (item: any, type: "folder" | "file") => {
    // スターが付いていれば、種類問わずスターアイコン（黄色）を表示
    if (item.isBookmarked) {
      return (
        <BookmarkIcon
          sx={{ color: "#FFB020", fontSize: type === "folder" ? 24 : 20 }}
        />
      );
    }
    // 制限付きフォルダはロックアイコン
    if (type === "folder" && item.isRestricted) {
      return <LockIcon sx={{ color: "#FF9800", fontSize: 24 }} />;
    }
    // 通常時のアイコン
    if (type === "folder") {
      return <Folder sx={{ color: "#FFB020", fontSize: 24 }} />;
    }
    return (
      <Box sx={{ color: "text.secondary", display: "flex" }}>
        <FileTypeIcon fileName={item.fileName ?? item.name ?? ""} size={20} />
      </Box>
    );
  };

  // 現在のフォルダが編集可能かどうか (制限なし or 編集者)
  const canEdit =
    data?.currentFolderAccessLevel === null ||
    data?.currentFolderAccessLevel === undefined ||
    data?.currentFolderAccessLevel >= 2;

  // --- 検索フィルタリング ---
  const filteredFolders = useMemo(
    () =>
      data?.folders?.filter((f) =>
        f.folderName.toLowerCase().includes(searchQuery.toLowerCase()),
      ) || [],
    [data?.folders, searchQuery],
  );

  const filteredFiles = useMemo(() => {
    const filtered =
      data?.files?.filter((f) =>
        f.fileName.toLowerCase().includes(searchQuery.toLowerCase()),
      ) || [];
    return [...filtered].sort((a, b) => {
      const dir = sortDirection === "asc" ? 1 : -1;
      switch (sortKey) {
        case "fileName":
          return dir * a.fileName.localeCompare(b.fileName, "ja");
        case "uploaderName":
          return (
            dir *
            (a.uploaderName ?? "").localeCompare(b.uploaderName ?? "", "ja")
          );
        case "updatedAt":
          return (
            dir *
            (new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime())
          );
        case "fileSize":
          return dir * (a.fileSize - b.fileSize);
        default:
          return 0;
      }
    });
  }, [data?.files, searchQuery, sortKey, sortDirection]);

  const isEmpty =
    !isLoading && filteredFolders.length === 0 && filteredFiles.length === 0;

  return (
    <Box
      sx={{
        p: isMobile ? 2 : 4,
        pt: isMobile ? 2 : 4,
        height: "100%",
        bgcolor: "background.default",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setIsNativeDragging(true);
      }}
    >
      {/* 外部ファイルドロップオーバーレイ */}
      <Fade in={isNativeDragging}>
        <Box
          onDragLeave={() => setIsNativeDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsNativeDragging(false);
            onUploadFiles(Array.from(e.dataTransfer.files));
          }}
          sx={{
            position: "absolute",
            inset: 12,
            zIndex: 1000,
            bgcolor: isDarkMode
              ? "rgba(15, 23, 42, 0.9)"
              : "rgba(255, 255, 255, 0.9)",
            border: `3px dashed ${accentColor}`,
            borderRadius: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(8px)",
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 80, color: accentColor, mb: 2 }} />
          <Typography
            variant="h5"
            sx={{ color: accentColor, fontWeight: "bold" }}
          >
            ファイルをドロップしてアップロード
          </Typography>
        </Box>
      </Fade>

      {/* ツールバー */}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ mb: isMobile ? 2 : 3 }}
      >
        {isMobile && onMenuClick && (
          <IconButton onClick={onMenuClick} sx={{ mr: 0.5 }}>
            <MenuIcon />
          </IconButton>
        )}
        <TextField
          placeholder="検索..."
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <IconButton size="small" onClick={() => setSearchQuery("")}>
                <CloseIcon fontSize="inherit" />
              </IconButton>
            ),
          }}
          sx={{ flexGrow: 1, maxWidth: isMobile ? "100%" : 400 }}
        />

        {!isMobile && (
          <>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, mode) => mode && onViewModeChange(mode)}
              size="small"
            >
              <ToggleButton value="grid">
                <GridViewIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="thumbnail">
                <WidgetsIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="list">
                <ListViewIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>

            {canEdit && (
              <>
                <Divider orientation="vertical" flexItem />
                <PopButton
                  palette={palette}
                  popVariant="primary"
                  startIcon={<UploadIcon />}
                  onClick={onUploadClick}
                >
                  アップロード
                </PopButton>
                <PopButton
                  palette={palette}
                  popVariant="white"
                  startIcon={<NewFolderIcon />}
                  onClick={onCreateFolderClick}
                >
                  新しいフォルダー
                </PopButton>
              </>
            )}
          </>
        )}
      </Stack>

      {/* パンくずリスト */}
      <Breadcrumbs separator={<ChevronRight fontSize="small" />} sx={{ mb: 3 }}>
        {data?.breadcrumbs.map((b, index) => (
          <Link
            key={index}
            component="button"
            underline="hover"
            color={
              index === data.breadcrumbs.length - 1
                ? "text.primary"
                : "text.secondary"
            }
            onClick={() => onFolderClick(b.id)}
            sx={{
              fontSize: "0.875rem",
              fontWeight: index === data.breadcrumbs.length - 1 ? 600 : 400,
              border: "none",
              background: "none",
              cursor: "pointer",
            }}
          >
            {b.name}
          </Link>
        ))}
      </Breadcrumbs>

      {/* 全フォルダ横断検索結果 */}
      {searchQuery.trim() && (
        <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
          {isSearching ? (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : searchResults &&
            (searchResults.folders.length > 0 ||
              searchResults.files.length > 0) ? (
            <>
              {/* フォルダ結果 */}
              {searchResults.folders.length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 600, px: 1 }}
                  >
                    フォルダ ({searchResults.folders.length})
                  </Typography>
                  <List dense disablePadding sx={{ mb: 1 }}>
                    {searchResults.folders.map((folder: SearchFolderItem) => (
                      <ListItem key={folder.folderId} disablePadding>
                        <ListItemButton
                          onClick={() => {
                            onFolderClick(folder.folderId);
                            setSearchQuery("");
                          }}
                          sx={{ borderRadius: 1, py: 0.5 }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {folder.isBookmarked ? (
                              <BookmarkIcon
                                sx={{ color: "#FFB020", fontSize: 20 }}
                              />
                            ) : folder.isRestricted ? (
                              <LockIcon
                                sx={{ color: "#FF9800", fontSize: 20 }}
                              />
                            ) : (
                              <Folder sx={{ color: "#FFB020", fontSize: 20 }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={folder.folderName}
                            secondary={folder.parentPath || "ルート"}
                            primaryTypographyProps={{ fontSize: "0.875rem" }}
                            secondaryTypographyProps={{ fontSize: "0.75rem" }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                  {searchResults.files.length > 0 && <Divider sx={{ my: 1 }} />}
                </>
              )}

              {/* ファイル結果 */}
              {searchResults.files.length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontWeight: 600, px: 1 }}
                  >
                    ファイル ({searchResults.files.length})
                  </Typography>
                  <List dense disablePadding>
                    {searchResults.files.map((file: SearchFileItem) => (
                      <ListItem key={file.fileId} disablePadding>
                        <ListItemButton
                          onClick={() => onFileClick(file)}
                          sx={{ borderRadius: 1, py: 0.5 }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {getThumbnailUrl(file) ? (
                              <FileThumbnail
                                src={getThumbnailUrl(file)!}
                                size={24}
                                fallback={
                                  file.isBookmarked ? (
                                    <BookmarkIcon
                                      sx={{ color: "#FFB020", fontSize: 20 }}
                                    />
                                  ) : (
                                    <FileTypeIcon
                                      fileName={file.fileName}
                                      size={20}
                                    />
                                  )
                                }
                              />
                            ) : file.isBookmarked ? (
                              <BookmarkIcon
                                sx={{ color: "#FFB020", fontSize: 20 }}
                              />
                            ) : (
                              <FileTypeIcon
                                fileName={file.fileName}
                                size={20}
                              />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={file.fileName}
                            secondary={
                              (file.folderPath
                                ? file.folderPath + " · "
                                : "ルート · ") +
                              formatFileSize(file.fileSize) +
                              " · " +
                              formatDate(file.updatedAt)
                            }
                            primaryTypographyProps={{ fontSize: "0.875rem" }}
                            secondaryTypographyProps={{ fontSize: "0.75rem" }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
              <Typography color="text.secondary">
                検索結果が見つかりませんでした
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* メインコンテンツ (検索中は非表示) */}
      {!searchQuery.trim() &&
        (isEmpty ? (
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CloudUploadIcon
              sx={{
                fontSize: 100,
                color: "divider",
                animation: `${float} 3s ease-in-out infinite`,
              }}
            />
            <Typography variant="h6" sx={{ mt: 2, color: "text.secondary" }}>
              表示する項目がありません
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
            {/* --- フォルダ一覧 (グリッド/リスト共通でカード形式) --- */}
            {filteredFolders.length > 0 && (
              <>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "repeat(auto-fill, minmax(280px, 1fr))",
                    gap: 2,
                    pb: 2,
                  }}
                >
                  {filteredFolders.map((folder) => (
                    <ItemCard
                      key={folder.folderId}
                      title={folder.folderName}
                      subTitle={formatDate(folder.createdAt)}
                      icon={folder.isRestricted ? <LockIcon /> : <Folder />}
                      isBookmarked={folder.isBookmarked}
                      palette={palette}
                      onClick={() =>
                        isSelectionMode
                          ? onToggleSelection?.(folder.folderId, "folder")
                          : onFolderClick(folder.folderId)
                      }
                      onActionClick={(e) => handleMenuOpen(e, folder)}
                      isSelectionMode={isSelectionMode}
                      isSelected={isItemSelected(folder.folderId)}
                    />
                  ))}
                </Box>
                {/* フォルダとファイルの間の区切り線 */}
                {filteredFiles.length > 0 && (
                  <Divider
                    sx={{ my: 2, borderColor: "divider", opacity: 0.5 }}
                  />
                )}
              </>
            )}

            {/* --- ファイルソートバー + 選択操作 --- */}
            {filteredFiles.length > 0 && (
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                {isSelectionMode ? (
                  <>
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={onBulkDownload}
                      disabled={selectedItems.length === 0}
                      sx={{ color: accentColor, textTransform: "none" }}
                    >
                      保存
                    </Button>
                    <Button
                      size="small"
                      startIcon={<BatchDeleteIcon />}
                      onClick={onBulkDelete}
                      disabled={selectedItems.length === 0}
                      sx={{ color: "error.main", textTransform: "none" }}
                    >
                      削除
                    </Button>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, color: "text.secondary" }}
                    >
                      {selectedItems.length}件選択中
                    </Typography>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                      size="small"
                      startIcon={<CloseIcon />}
                      onClick={onExitSelectionMode}
                      sx={{ color: "text.secondary", textTransform: "none" }}
                    >
                      キャンセル
                    </Button>
                  </>
                ) : (
                  <>
                    <FormControl size="small" sx={{ minWidth: 0 }}>
                      <Select
                        value={sortKey}
                        onChange={(e) => handleSort(e.target.value as SortKey)}
                        variant="standard"
                        disableUnderline
                        sx={{
                          fontSize: "0.8rem",
                          color: "text.secondary",
                          "& .MuiSelect-select": {
                            py: 0,
                            pr: "20px !important",
                          },
                        }}
                      >
                        {(Object.keys(sortKeyLabels) as SortKey[]).map(
                          (key) => (
                            <MenuItem key={key} value={key} dense>
                              {sortKeyLabels[key]}
                            </MenuItem>
                          ),
                        )}
                      </Select>
                    </FormControl>
                    <IconButton
                      size="small"
                      onClick={() =>
                        setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
                      }
                      sx={{ color: "text.secondary" }}
                    >
                      {sortDirection === "asc" ? (
                        <AscIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <DescIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                      size="small"
                      startIcon={<SelectIcon />}
                      onClick={onEnterSelectionMode}
                      sx={{ color: "text.secondary", textTransform: "none" }}
                    >
                      選択
                    </Button>
                  </>
                )}
              </Stack>
            )}
            {filteredFiles.length > 0 &&
              (viewMode === "grid" ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr"
                      : "repeat(auto-fill, minmax(280px, 1fr))",
                    gap: 2,
                    pb: 4,
                  }}
                >
                  {filteredFiles.map((file) => (
                    <ItemCard
                      key={file.fileId}
                      title={file.fileName}
                      subTitle={`${new Date(file.updatedAt).getMonth() + 1}/${new Date(file.updatedAt).getDate()} ${new Date(file.updatedAt).getHours()}:${String(new Date(file.updatedAt).getMinutes()).padStart(2, "0")} ${file.uploaderName ?? ""}`}
                      icon={<FileTypeIcon fileName={file.fileName} size={32} />}
                      isBookmarked={file.isBookmarked}
                      thumbnailUrl={getThumbnailUrl(file)}
                      palette={palette}
                      onClick={() =>
                        isSelectionMode
                          ? onToggleSelection?.(file.fileId, "file")
                          : onFileClick(file)
                      }
                      actionIcon={<InfoIcon fontSize="small" />}
                      onActionClick={(e) => {
                        onShowDetail(file);
                      }}
                      isSelectionMode={isSelectionMode}
                      isSelected={isItemSelected(file.fileId)}
                    />
                  ))}
                </Box>
              ) : viewMode === "thumbnail" ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                    gap: 1.5,
                    pb: 4,
                  }}
                >
                  {filteredFiles.map((file) => (
                    <Paper
                      key={file.fileId}
                      variant="outlined"
                      onClick={() =>
                        isSelectionMode
                          ? onToggleSelection?.(file.fileId, "file")
                          : onFileClick(file)
                      }
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        cursor: "pointer",
                        transition: "box-shadow 0.15s",
                        "&:hover": { boxShadow: 3 },
                        ...(isSelectionMode && isItemSelected(file.fileId)
                          ? {
                              borderColor: accentColor,
                              boxShadow: `0 0 0 1px ${accentColor}`,
                            }
                          : {}),
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          aspectRatio: "1 / 1",
                          bgcolor: isDarkMode ? "grey.900" : "grey.100",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {getThumbnailUrl(file) &&
                        !thumbErrorIds.has(file.fileId) ? (
                          <Box
                            component="img"
                            src={getThumbnailUrl(file)}
                            alt={file.fileName}
                            loading="lazy"
                            onError={() =>
                              setThumbErrorIds((prev) =>
                                new Set(prev).add(file.fileId),
                              )
                            }
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <Box sx={{ color: "text.secondary", display: "flex" }}>
                            <FileTypeIcon fileName={file.fileName} size={56} />
                          </Box>
                        )}
                        {isSelectionMode && (
                          <Checkbox
                            checked={isItemSelected(file.fileId)}
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 4,
                              left: 4,
                              p: 0.5,
                              borderRadius: "50%",
                              bgcolor: isDarkMode
                                ? "rgba(0,0,0,0.5)"
                                : "rgba(255,255,255,0.85)",
                              color: accentColor,
                              "&.Mui-checked": { color: accentColor },
                            }}
                          />
                        )}
                        {file.isBookmarked && (
                          <BookmarkIcon
                            sx={{
                              position: "absolute",
                              top: 6,
                              right: 6,
                              fontSize: 20,
                              color: "#FFB020",
                              filter:
                                "drop-shadow(0 0 2px rgba(0,0,0,0.4))",
                              pointerEvents: "none",
                            }}
                          />
                        )}
                      </Box>
                      <Box sx={{ p: 1 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                        >
                          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                            <Typography
                              variant="caption"
                              noWrap
                              display="block"
                              title={file.fileName}
                            >
                              {file.fileName}
                            </Typography>
                            <Typography
                              variant="caption"
                              noWrap
                              display="block"
                              sx={{
                                color: "text.secondary",
                                fontSize: "0.65rem",
                              }}
                            >
                              {`${new Date(file.updatedAt).getMonth() + 1}/${new Date(file.updatedAt).getDate()} ${new Date(file.updatedAt).getHours()}:${String(new Date(file.updatedAt).getMinutes()).padStart(2, "0")} ${file.uploaderName ?? ""}`}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onShowDetail(file);
                            }}
                          >
                            <InfoIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Stack>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead
                      sx={{
                        bgcolor: isDarkMode
                          ? "rgba(255,255,255,0.05)"
                          : "#f8f9fa",
                      }}
                    >
                      <TableRow>
                        {isSelectionMode && <TableCell padding="checkbox" />}
                        <TableCell
                          sortDirection={
                            sortKey === "fileName" ? sortDirection : false
                          }
                        >
                          <TableSortLabel
                            active={sortKey === "fileName"}
                            direction={
                              sortKey === "fileName" ? sortDirection : "asc"
                            }
                            onClick={() => handleSort("fileName")}
                          >
                            名前
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sortDirection={
                            sortKey === "uploaderName" ? sortDirection : false
                          }
                        >
                          <TableSortLabel
                            active={sortKey === "uploaderName"}
                            direction={
                              sortKey === "uploaderName" ? sortDirection : "asc"
                            }
                            onClick={() => handleSort("uploaderName")}
                          >
                            アップロード
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          sortDirection={
                            sortKey === "updatedAt" ? sortDirection : false
                          }
                        >
                          <TableSortLabel
                            active={sortKey === "updatedAt"}
                            direction={
                              sortKey === "updatedAt" ? sortDirection : "asc"
                            }
                            onClick={() => handleSort("updatedAt")}
                          >
                            更新日
                          </TableSortLabel>
                        </TableCell>
                        <TableCell
                          align="right"
                          sortDirection={
                            sortKey === "fileSize" ? sortDirection : false
                          }
                        >
                          <TableSortLabel
                            active={sortKey === "fileSize"}
                            direction={
                              sortKey === "fileSize" ? sortDirection : "asc"
                            }
                            onClick={() => handleSort("fileSize")}
                          >
                            サイズ
                          </TableSortLabel>
                        </TableCell>
                        <TableCell align="right">操作</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredFiles.map((file) => (
                        <TableRow
                          key={file.fileId}
                          hover
                          selected={
                            isSelectionMode && isItemSelected(file.fileId)
                          }
                          onClick={() =>
                            isSelectionMode
                              ? onToggleSelection?.(file.fileId, "file")
                              : onFileClick(file)
                          }
                          sx={{ cursor: "pointer" }}
                        >
                          {isSelectionMode && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isItemSelected(file.fileId)}
                                size="small"
                                sx={{
                                  color: accentColor,
                                  "&.Mui-checked": { color: accentColor },
                                }}
                              />
                            </TableCell>
                          )}
                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={1.5}
                              alignItems="center"
                            >
                              {getThumbnailUrl(file) ? (
                                <FileThumbnail
                                  src={getThumbnailUrl(file)!}
                                  size={28}
                                  fallback={getItemIcon(file, "file")}
                                />
                              ) : (
                                getItemIcon(file, "file")
                              )}
                              <Typography variant="body2">
                                {file.fileName}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {file.uploaderName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatDate(file.updatedAt)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="caption">
                              {formatFileSize(file.fileSize)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                onShowDetail(file);
                              }}
                            >
                              <InfoIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ))}
          </Box>
        ))}

      {/* 操作メニュー */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        TransitionComponent={Fade}
      >
        {canEdit && (
          <MenuItem
            onClick={() => {
              const id = menuTarget?.folderId || menuTarget?.fileId;
              const name = menuTarget?.folderName || menuTarget?.fileName;
              const type = menuTarget?.folderId ? "folder" : "file";
              onRenameClick(id, name, type);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            名前を変更
          </MenuItem>
        )}

        {/* フォルダの場合: アクセス制限メニュー */}
        {menuTarget?.folderId && onOpenPermission && (
          <MenuItem
            onClick={() => {
              onOpenPermission(
                menuTarget.folderId,
                menuTarget.folderName,
                menuTarget.isRestricted ?? false,
              );
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              {menuTarget.isRestricted ? (
                <LockIcon fontSize="small" sx={{ color: "warning.main" }} />
              ) : (
                <LockOpenIcon fontSize="small" />
              )}
            </ListItemIcon>
            アクセス制限
          </MenuItem>
        )}

        {canEdit && (
          <MenuItem
            onClick={() => {
              const id = menuTarget?.folderId || menuTarget?.fileId;
              const type = menuTarget?.folderId ? "folder" : "file";
              onDeleteItem(id, type);
              handleMenuClose();
            }}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            削除
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
